import { useEffect, useMemo, useState } from 'react';
import { SEDE_CODES } from '@lidotel/shared';
import { api, type RoomTypeStatExtraction } from '../../lib/api.js';
import { fmtBs, fmtInt } from '../../lib/format.js';

const empty: RoomTypeStatExtraction = {
  sede_codigo: null,
  room_type: null,
  descripcion: null,
  dias: [],
  grand_total: null,
};

/**
 * Revisión de «Statistics - Room Type»: PDF a la izquierda, días a la derecha.
 *
 * Arriba de todo va el cuadre `Σ días = Grand Total`, que es lo único que
 * distingue «se perdió una fila» de «ese tipo se vendió poco»; el backend lo
 * bloquea si no cuadra.
 *
 * Los días que el reporte NO imprime no se rellenan con ceros: decir que ese día
 * no se vendió nada es una afirmación que el reporte no hace. Se avisa de cuántos
 * faltan para que el revisor lo juzgue.
 */
export function RoomTypeReviewPanel({
  id,
  onCommitted,
  onClose,
}: {
  id: string;
  onCommitted: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<RoomTypeStatExtraction>(empty);
  const [rangos, setRangos] = useState<
    { room_type: string; desde: string; hasta: string; dias: number; ocupadas: number }[]
  >([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [needsExtract, setNeedsExtract] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  function hydrate(d: RoomTypeStatExtraction) {
    setForm({
      sede_codigo: d.sede_codigo ?? null,
      room_type: d.room_type ?? null,
      descripcion: d.descripcion ?? null,
      dias: d.dias ?? [],
      grand_total: d.grand_total ?? null,
    });
  }

  useEffect(() => {
    let url: string | null = null;
    api
      .ingestaGet(id)
      .then((r) => {
        if (r.extracted && 'dias' in r.extracted && Array.isArray(r.extracted.dias)) {
          hydrate(r.extracted as RoomTypeStatExtraction);
        } else setNeedsExtract(true);
        setRangos(r.rangosCargados ?? []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
    api
      .ingestaFileUrl(id)
      .then((u) => {
        url = u;
        setPdfUrl(u);
      })
      .catch(() => setPdfUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  async function extract() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaExtract(id);
      if (r.tipo === 'roomtype') hydrate(r.extracted as RoomTypeStatExtraction);
      setNeedsExtract(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de extracción');
    } finally {
      setBusy(false);
    }
  }

  function setCelda(i: number, campo: 'potential' | 'ocupadas' | 'personas', raw: string) {
    const v = raw === '' ? null : Number(raw);
    setForm((f) => ({
      ...f,
      dias: f.dias.map((d, j) => (j === i ? { ...d, [campo]: v } : d)),
    }));
  }

  async function commit(force: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaCommitRoomType(id, form, force);
      setIssues(r.issues ?? []);
      if (r.committed) onCommitted();
      else if (r.error) {
        setError(r.error);
        setBlocked(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al confirmar');
    } finally {
      setBusy(false);
    }
  }

  const suma = useMemo(
    () => ({
      potential: form.dias.reduce((s, d) => s + (d.potential ?? 0), 0),
      ocupadas: form.dias.reduce((s, d) => s + (d.ocupadas ?? 0), 0),
      revenue: form.dias.reduce((s, d) => s + (d.room_revenue_bs ?? 0), 0),
    }),
    [form.dias],
  );
  const gt = form.grand_total;
  const cuadra = gt?.ocupadas == null || suma.ocupadas === gt.ocupadas;
  const fechas = form.dias
    .map((d) => d.fecha)
    .filter(Boolean)
    .sort();
  const rango = fechas.length ? `${fechas[0]} → ${fechas[fechas.length - 1]}` : 'sin rango';
  const yaCargado = rangos.find((r) => r.room_type === form.room_type);
  // Días del calendario que el reporte no imprimió: ausencia, no cero.
  const faltan =
    fechas.length > 1
      ? (Date.parse(fechas[fechas.length - 1]!) - Date.parse(fechas[0]!)) / 86400000 +
        1 -
        fechas.length
      : 0;

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-200">
          Revisión Statistics - Room Type
          <span className="ml-2 rounded bg-teal-900 px-1.5 py-0.5 text-[10px] text-teal-200">
            {form.room_type ?? '—'} · {rango}
          </span>
        </h2>
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          Cerrar ✕
        </button>
      </div>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-950/60 px-3 py-2 text-sm text-rose-300">{error}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-[420px] overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Statistics - Room Type"
              className="h-[420px] w-full lg:h-[640px]"
            />
          ) : (
            <p className="p-4 text-sm text-slate-500">Vista previa no disponible.</p>
          )}
        </div>

        <div>
          {needsExtract && (
            <div className="mb-4">
              <p className="mb-2 text-sm text-slate-400">Aún no se ha extraído este reporte.</p>
              <button
                onClick={extract}
                disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {busy ? 'Extrayendo…' : 'Extraer con Gemini'}
              </button>
            </div>
          )}

          <div className="mb-3 flex flex-wrap gap-3 text-xs text-slate-400">
            <label>
              Sede
              <select
                value={form.sede_codigo ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, sede_codigo: e.target.value || null }))}
                className="mt-1 block w-32 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              >
                <option value="">—</option>
                {SEDE_CODES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Tipo
              <input
                value={form.room_type ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, room_type: e.target.value || null }))}
                className="mt-1 block w-24 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm uppercase text-slate-100"
              />
            </label>
            <label className="min-w-0 flex-1">
              Descripción
              <input
                value={form.descripcion ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value || null }))}
                className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              />
            </label>
          </div>

          {form.dias.length === 0 ? (
            <p className="text-sm text-slate-500">
              Sin días detectados. Extrae el reporte primero.
            </p>
          ) : (
            <>
              <div className="max-h-[320px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-500">
                      <th className="px-1 py-1 text-left">Día</th>
                      <th className="px-1 py-1 text-right">Aforo</th>
                      <th className="px-1 py-1 text-right">Ocupadas</th>
                      <th className="px-1 py-1 text-right">Prs.</th>
                      <th className="px-1 py-1 text-right">Revenue Bs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.dias.map((d, i) => (
                      <tr key={`${d.fecha}-${i}`} className="border-t border-slate-800">
                        <td className="px-1 py-1 font-mono text-slate-300">{d.fecha?.slice(5)}</td>
                        {(['potential', 'ocupadas', 'personas'] as const).map((campo) => (
                          <td key={campo} className="px-1 py-1 text-right">
                            <input
                              type="number"
                              min="0"
                              value={d[campo] ?? ''}
                              onChange={(e) => setCelda(i, campo, e.target.value)}
                              className="w-14 rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-right text-slate-100"
                            />
                          </td>
                        ))}
                        <td className="px-1 py-1 text-right tabular-nums text-slate-400">
                          {d.room_revenue_bs == null ? '—' : fmtBs(d.room_revenue_bs)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-2 text-xs">
                <p className={cuadra ? 'text-slate-400' : 'font-medium text-amber-300'}>
                  Σ días <span className="tabular-nums">{fmtInt(suma.ocupadas)}</span> ocupadas ·
                  Grand Total <span className="tabular-nums">{fmtInt(gt?.ocupadas ?? null)}</span>{' '}
                  {cuadra ? '· cuadra ✓' : `· descuadre de ${suma.ocupadas - (gt?.ocupadas ?? 0)}`}
                </p>
                <p className="mt-1 text-slate-500">
                  {form.dias.length} día(s) impresos · aforo {fmtInt(suma.potential)} · revenue{' '}
                  {fmtBs(suma.revenue)}
                </p>
                {faltan > 0 && (
                  <p className="mt-1 text-slate-500">
                    {faltan} día(s) del rango no aparecen en el reporte: se dejan SIN fila, no en
                    cero.
                  </p>
                )}
                {yaCargado && (
                  <p className="mt-1 text-amber-200">
                    {yaCargado.room_type} ya tiene {yaCargado.dias} día(s) cargados (
                    {yaCargado.desde} → {yaCargado.hasta}); confirmar reemplaza los de este rango.
                  </p>
                )}
              </div>
            </>
          )}

          {issues.length > 0 && (
            <ul className="mt-3 space-y-1">
              {issues.map((it, idx) => (
                <li
                  key={idx}
                  className="rounded-md bg-amber-950/50 px-2 py-1 text-xs text-amber-200"
                >
                  {it}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => commit(false)}
              disabled={busy || needsExtract || form.dias.length === 0}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? 'Confirmando…' : `Confirmar ${form.dias.length} día(s)`}
            </button>
            {blocked && (
              <button
                onClick={() => commit(true)}
                disabled={busy}
                title="Guarda los valores tal cual, saltándose el cuadre contra el Grand Total"
                className="rounded-md border border-amber-600 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-950/40 disabled:opacity-50"
              >
                Confirmar de todos modos
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
